/* eslint max-lines: ["error", 700] */
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import TextCopyInput from '../../../base-ui/components/TextCopyInput';
import useQuery from '../../../core/hooks/useQuery';
import { toDateTime } from '../../../domain/date';
import createInvoiceCode from '../../../invoice/automation/createInvoice.js?raw';
import SpinnerIcon from '../../../main/component/icon/SpinnerIcon';
import { calculateInvoiceItems } from '../../calculation/invoiceBreakdown';
import useAlterBookingState from '../../hooks/useAlterBookingState';

interface SzamlazzImportData {
    reference: string;
    guestName: string;
    countryName: string;
    postcode: string;
    address: string;
    paymentDate: string;
    accommodationPrice: number;
    touristTax: number;
    accommodationDescription: string;
}

interface SzamlazzImportResponse {
    success: boolean;
    error?: string;
}

interface QueueAddResponse {
    success: boolean;
    error?: string;
    command?: {
        id: string;
        name: string;
    };
}

declare global {
    interface Window {
        __MUSHROOM_EXTENSION_ID__?: string;
    }
}

interface ChromeRuntime {
    sendMessage: (
        extensionId: string,
        message: { type: string; data: SzamlazzImportData },
        callback: (response: SzamlazzImportResponse) => void
    ) => void;
}

interface Chrome {
    runtime?: ChromeRuntime;
}

const getExtensionId = (): string | null => {
    return typeof window !== 'undefined' ? window.__MUSHROOM_EXTENSION_ID__ ?? null : null;
};

const sendSzamlazzImport = (data: SzamlazzImportData): Promise<SzamlazzImportResponse> => {
    return new Promise((resolve) => {
        const extensionId = getExtensionId();
        if (!extensionId) {
            resolve({ success: false, error: 'Extension not detected' });
            return;
        }
        const chromeApi = (globalThis as unknown as { chrome?: Chrome }).chrome;
        if (!chromeApi?.runtime?.sendMessage) {
            resolve({ success: false, error: 'Chrome runtime not available' });
            return;
        }
        chromeApi.runtime.sendMessage(
            extensionId,
            { type: 'SZAMLAZZ_IMPORT', data },
            (response) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve({ success: false, error: 'No response from extension' });
                }
            }
        );
    });
};

const sendSzamlazzQueueCommand = (data: SzamlazzImportData, bookingId: number): Promise<QueueAddResponse> => {
    return new Promise((resolve) => {
        const extensionId = getExtensionId();
        if (!extensionId) {
            resolve({ success: false, error: 'Extension not detected' });
            return;
        }
        const chromeRuntime = (globalThis as { chrome?: { runtime?: { sendMessage: (
            extensionId: string,
            message: unknown,
            callback: (response: QueueAddResponse) => void
        ) => void } } }).chrome;
        if (!chromeRuntime?.runtime?.sendMessage) {
            resolve({ success: false, error: 'Chrome runtime not available' });
            return;
        }
        const message = {
            type: 'QUEUE_ADD',
            commandType: 'SZAMLAZZ_IMPORT',
            name: data.reference,
            targetDomain: 'szamlazz.hu',
            targetUrl: 'https://www.szamlazz.hu/szamla/?page=szamlaszerkeszto',
            data,
            sourceBookingId: bookingId,
        };
        chromeRuntime.runtime.sendMessage(
            extensionId,
            message,
            (response) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve({ success: false, error: 'No response from extension' });
                }
            }
        );
    });
};

const invoiceViewQuery = gql`
    query InvoiceViewDialogQuery($id: Int!) {
        booking(id: $id) {
            id
            reference
            guestName
            revenue
            cleaningFee
            checkin
            checkout
            guests {
                id
                firstName
                lastName
                countryNameEN
                birthDate
                postcode
                address
                price
                touristTax
            }
        }
    }
`;

type InvoiceViewResult = {
    booking: {
        id: number;
        reference: string;
        guestName: string;
        revenue: number;
        cleaningFee: number;
        checkin: string;
        checkout: string;
        guests: {
            id: number;
            firstName: string;
            lastName: string;
            birthDate: string;
            countryNameEN: string;
            postcode: string;
            address: string;
            price: number;
            touristTax: number;
        }[];
    };
};

const TIMES_SYMBOL = '×';

/* eslint no-mixed-operators: 0 */
export const InvoiceViewDialog = ({
    bookingId,
    onFocusLost,
    onSuccess,
}: {
    bookingId: number;
    onFocusLost: () => void;
    onSuccess?: () => void;
}) => {
    const client = useApolloClient();
    const [saving, setSaving] = useState(false);
    const [invoiceId, setInvoiceId] = useState('');
    const [extensionInstalled, setExtensionInstalled] = useState(false);
    const [importStatus, setImportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [importError, setImportError] = useState<string | null>(null);
    const [showManualFallback, setShowManualFallback] = useState(false);
    const [queueStatus, setQueueStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [queueError, setQueueError] = useState<string | null>(null);
    const [alterBookingState] = useAlterBookingState(bookingId);
    const { loading, data, error } = useQuery<InvoiceViewResult>(
        invoiceViewQuery,
        { variables: { id: bookingId } }
    );

    useEffect(() => {
        // Check for extension after a short delay to allow content script to inject
        const timer = setTimeout(() => {
            setExtensionInstalled(getExtensionId() !== null);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    if (error) {
        return <Dialog open>{(error as Error).toString()}</Dialog>;
    }

    if (loading) {
        return <SpinnerIcon />;
    }

    if (saving) {
        return <SpinnerIcon />;
    }

    const { booking } = data;
    const { revenue, checkin, checkout, guests } = booking;
    const [{ firstName, lastName, countryNameEN, postcode, address }] = guests;

    const invoiceItems = calculateInvoiceItems(booking);
    const nightCount = checkout
        ? toDateTime(checkout).diff(toDateTime(checkin), 'days').days
        : 0;

    /* spellchecker: disable */
    return (
        <Dialog
            open
            onClose={onFocusLost}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #6366f1',
                        backgroundColor: '#1a1a2e',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 2.5,
                }}
            >
                Invoice Details
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: '#1a1a2e', p: 0 }}>
                <Stack sx={{ py: 3, px: 3 }} spacing={3}>
                    {/* Customer Info Section */}
                    <Box
                        sx={{
                            border: '2px solid #374151',
                            backgroundColor: '#0f0f23',
                            p: 2.5,
                        }}
                    >
                        <Box
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: '#6366f1',
                                mb: 2,
                            }}
                        >
                            Customer Information
                        </Box>
                        <Stack spacing={1.5}>
                            <Box display="flex" gap={2}>
                                <Box
                                    sx={{
                                        minWidth: 120,
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#9ca3af',
                                    }}
                                >
                                    Customer
                                </Box>
                                <Box
                                    sx={{ flex: 1, fontWeight: 600 }}
                                >{`${firstName} ${lastName}`}</Box>
                            </Box>
                            <Box display="flex" gap={2}>
                                <Box
                                    sx={{
                                        minWidth: 120,
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#9ca3af',
                                    }}
                                >
                                    Reference
                                </Box>
                                <Box sx={{ flex: 1, fontWeight: 600 }}>
                                    {booking.reference}
                                </Box>
                            </Box>
                            <Box display="flex" gap={2}>
                                <Box
                                    sx={{
                                        minWidth: 120,
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#9ca3af',
                                    }}
                                >
                                    Stay
                                </Box>
                                <Box sx={{ flex: 1, fontWeight: 600 }}>
                                    {`${guests.length} guest${guests.length > 1 ? 's' : ''} ${TIMES_SYMBOL} ${nightCount} night${nightCount > 1 ? 's' : ''}`}
                                </Box>
                            </Box>
                            <Box display="flex" gap={2}>
                                <Box
                                    sx={{
                                        minWidth: 120,
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#9ca3af',
                                    }}
                                >
                                    Address
                                </Box>
                                <Box sx={{ flex: 1, fontWeight: 600 }}>
                                    {address}, {postcode}, {countryNameEN}
                                </Box>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Invoice Breakdown Section */}
                    <Box
                        sx={{
                            border: '2px solid #374151',
                            backgroundColor: '#0f0f23',
                            p: 2.5,
                        }}
                    >
                        <Box
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: '#6366f1',
                                mb: 2,
                            }}
                        >
                            Invoice Breakdown
                        </Box>
                        <Stack spacing={1}>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                gap={2}
                            >
                                <Box sx={{ color: '#9ca3af' }}>
                                    Accommodation
                                </Box>
                                <Box sx={{ fontWeight: 600 }}>
                                    {invoiceItems.accommodationPrice.toFixed(2)}{' '}
                                    Ft
                                </Box>
                            </Box>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                gap={2}
                            >
                                <Box sx={{ color: '#9ca3af' }}>Cleaning Fee</Box>
                                <Box sx={{ fontWeight: 600 }}>
                                    {invoiceItems.cleaningFee.toFixed(2)} Ft
                                </Box>
                            </Box>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                gap={2}
                            >
                                <Box sx={{ color: '#9ca3af' }}>Tourist Tax</Box>
                                <Box sx={{ fontWeight: 600 }}>
                                    {invoiceItems.touristTax.toFixed(2)} Ft
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    borderTop: '3px solid #6366f1',
                                    mt: 2,
                                    pt: 2,
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    gap={2}
                                >
                                    <Box
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                        }}
                                    >
                                        Total Revenue
                                    </Box>
                                    <Box
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: '1.25rem',
                                            color: '#22c55e',
                                        }}
                                    >
                                        {revenue.toFixed(2)} Ft
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Generate Invoice Section */}
                    <Box
                        sx={{
                            border: '2px solid #374151',
                            backgroundColor: '#0f0f23',
                            p: 2.5,
                        }}
                    >
                        <Box
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: '#6366f1',
                                mb: 2,
                            }}
                        >
                            Generate Invoice
                        </Box>
                        <Stack spacing={2}>
                            {/* Extension Import UI */}
                            {extensionInstalled && !showManualFallback && (
                                <>
                                    {importStatus === 'error' && importError && (
                                        <Alert
                                            severity="error"
                                            onClose={() => setImportError(null)}
                                            sx={{
                                                borderRadius: 0,
                                                border: '3px solid #ef4444',
                                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                fontWeight: 600,
                                                '& .MuiAlert-icon': {
                                                    color: '#ef4444',
                                                },
                                            }}
                                        >
                                            {importError}
                                        </Alert>
                                    )}
                                    {importStatus === 'success' && (
                                        <Alert
                                            severity="success"
                                            sx={{
                                                borderRadius: 0,
                                                border: '3px solid #22c55e',
                                                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                                fontWeight: 600,
                                                '& .MuiAlert-icon': {
                                                    color: '#22c55e',
                                                },
                                            }}
                                        >
                                            Invoice form filled successfully
                                        </Alert>
                                    )}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#9ca3af',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        MushRoom Extension detected. Click below to fill the invoice form automatically.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={async () => {
                                            setImportStatus('loading');
                                            setImportError(null);

                                            const response = await sendSzamlazzImport({
                                                reference: booking.reference,
                                                guestName: `${firstName} ${lastName}`,
                                                countryName: countryNameEN,
                                                postcode,
                                                address,
                                                paymentDate: new Date().toISOString().substring(0, 10),
                                                accommodationPrice: invoiceItems.accommodationPrice,
                                                touristTax: invoiceItems.touristTax,
                                                accommodationDescription: `${guests.length} vendégnek ${nightCount} éjszakára, takarítással`,
                                            });

                                            if (response.success) {
                                                setImportStatus('success');
                                            } else {
                                                setImportStatus('error');
                                                setImportError(response.error ?? 'Import failed');
                                            }
                                        }}
                                        disabled={importStatus === 'loading' || importStatus === 'success'}
                                        startIcon={importStatus === 'loading' ? <SpinnerIcon /> : undefined}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            borderRadius: 0,
                                            border: '2px solid',
                                            borderColor: 'secondary.main',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            px: 3,
                                            py: 1.5,
                                        }}
                                    >
                                        {importStatus === 'loading' ? 'Opening...' : 'Fill Invoice via Extension'}
                                    </Button>
                                    {queueStatus === 'error' && queueError && (
                                        <Alert
                                            severity="error"
                                            onClose={() => setQueueError(null)}
                                            sx={{
                                                borderRadius: 0,
                                                border: '3px solid #ef4444',
                                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                fontWeight: 600,
                                                '& .MuiAlert-icon': {
                                                    color: '#ef4444',
                                                },
                                            }}
                                        >
                                            {queueError}
                                        </Alert>
                                    )}
                                    {queueStatus === 'success' && (
                                        <Alert
                                            severity="success"
                                            sx={{
                                                borderRadius: 0,
                                                border: '3px solid #22c55e',
                                                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                                fontWeight: 600,
                                                '& .MuiAlert-icon': {
                                                    color: '#22c55e',
                                                },
                                            }}
                                        >
                                            Invoice queued to extension
                                        </Alert>
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={async () => {
                                            setQueueStatus('loading');
                                            setQueueError(null);

                                            const response = await sendSzamlazzQueueCommand({
                                                reference: booking.reference,
                                                guestName: `${firstName} ${lastName}`,
                                                countryName: countryNameEN,
                                                postcode,
                                                address,
                                                paymentDate: new Date().toISOString().substring(0, 10),
                                                accommodationPrice: invoiceItems.accommodationPrice,
                                                touristTax: invoiceItems.touristTax,
                                                accommodationDescription: `${guests.length} vendégnek ${nightCount} éjszakára, takarítással`,
                                            }, booking.id);

                                            if (response.success) {
                                                setQueueStatus('success');
                                            } else {
                                                setQueueStatus('error');
                                                setQueueError(response.error ?? 'Queue failed');
                                            }
                                        }}
                                        disabled={queueStatus === 'loading' || queueStatus === 'success'}
                                        startIcon={queueStatus === 'loading' ? <SpinnerIcon /> : undefined}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            borderRadius: 0,
                                            border: '2px solid',
                                            borderColor: 'secondary.main',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            px: 3,
                                            py: 1.5,
                                        }}
                                    >
                                        {queueStatus === 'loading' ? 'Queuing...' : queueStatus === 'success' ? 'Queued' : 'Queue to Extension'}
                                    </Button>
                                    <Button
                                        variant="text"
                                        onClick={() => setShowManualFallback(true)}
                                        sx={{
                                            color: '#6b7280',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.75rem',
                                            alignSelf: 'flex-start',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                color: '#9ca3af',
                                            },
                                        }}
                                    >
                                        Use manual import instead
                                    </Button>
                                </>
                            )}

                            {/* Manual Fallback UI */}
                            {(!extensionInstalled || showManualFallback) && (
                                <>
                                    {showManualFallback && (
                                        <Button
                                            variant="text"
                                            onClick={() => setShowManualFallback(false)}
                                            sx={{
                                                color: '#6b7280',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                fontSize: '0.75rem',
                                                alignSelf: 'flex-start',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                    color: '#9ca3af',
                                                },
                                            }}
                                        >
                                            ← Back to extension import
                                        </Button>
                                    )}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#9ca3af',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        {!extensionInstalled
                                            ? 'Install the MushRoom extension for automatic import, or copy and paste this script into the browser console on szamlazz.hu'
                                            : 'Copy and paste this script into the browser console on szamlazz.hu'}
                                    </Typography>
                                    <TextCopyInput
                                        type="textarea"
                                        value={`${createInvoiceCode}run(${JSON.stringify(
                                            {
                                                reference: booking.reference,
                                                guestName: `${firstName} ${lastName}`,
                                                paymentDate: new Date()
                                                    .toISOString()
                                                    .substring(0, 10),
                                                invoiceItems,
                                                countryName: countryNameEN,
                                                postcode,
                                                address,
                                                accommodationItemDescription: `${guests.length} vendégnek ${nightCount} éjszakára, takarítással`,
                                            }
                                        )});`}
                                    />
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            window.open(
                                                'https://www.szamlazz.hu/szamla/?page=szamlaszerkeszto',
                                                '_blank',
                                                'noopener'
                                            );
                                        }}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            borderRadius: 0,
                                            border: '2px solid',
                                            borderColor: 'secondary.main',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            px: 3,
                                            py: 1,
                                        }}
                                    >
                                        Open Szamlazz.hu
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Box>

                    {/* Invoice ID Input */}
                    <TextField
                        label="Invoice ID"
                        value={invoiceId}
                        onChange={event => {
                            setInvoiceId(event.target.value);
                        }}
                        placeholder="Enter invoice ID from Szamlazz.hu"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                '& input': {
                                    backgroundColor: '#0f0f23',
                                },
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: '#6366f1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '3px',
                                    borderColor: '#6366f1',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '0.875rem',
                                letterSpacing: '0.05em',
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '3px solid #374151',
                    backgroundColor: '#0f0f23',
                    p: 3,
                    gap: 1.5,
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onFocusLost}
                    sx={{
                        borderRadius: 0,
                        borderWidth: '2px',
                        borderColor: '#374151',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                        '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#374151',
                            backgroundColor: 'rgba(55, 65, 81, 0.1)',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!invoiceId || !booking.id}
                    onClick={() => {
                        setSaving(true);
                        alterBookingState({
                            variables: {
                                id: booking.id,
                                fields: {
                                    invoiceId,
                                },
                            },
                        })
                            .then(async () => {
                                await client.resetStore();
                                onSuccess?.();
                            })
                            .catch(saveError => {
                                throw saveError;
                            })
                            .finally(() => {
                                setSaving(false);
                                onFocusLost();
                            });
                    }}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid #6366f1',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                    }}
                >
                    Save Invoice
                </Button>
            </DialogActions>
        </Dialog>
    );
    /* spellchecker: enable */
};

const InvoiceView = ({ bookingId }: { bookingId: number }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                variant="contained"
                color="primary"
                onClick={() => {
                    setOpen(true);
                }}
                sx={{
                    borderRadius: 0,
                    border: '2px solid #6366f1',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 3,
                    py: 1,
                }}
            >
                Invoice View
            </Button>
            {open && (
                <InvoiceViewDialog
                    bookingId={bookingId}
                    onFocusLost={() => {
                        setOpen(false);
                    }}
                />
            )}
        </>
    );
};

export default InvoiceView;
